import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useOwnerAuth } from "./OwnerAuthContext";
import { Loader2, ShieldCheck } from "lucide-react";

export default function OwnerLogin() {
  const { owner, login } = useOwnerAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (owner) return <Navigate to="/owner/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    const res = await login(email.trim(), password);
    setBusy(false);
    if (res.ok) nav("/owner/dashboard", { replace: true });
    else setErr(res.error || "Login failed");
  };

  return (
    <div className="min-h-screen bg-[#040b1d] flex items-center justify-center px-4" data-testid="owner-login-page">
      <div className="absolute top-0 inset-x-0 h-[1px] bg-gradient-to-r from-transparent via-[#10b7c0]/40 to-transparent" />
      <form
        onSubmit={submit}
        className="relative w-full max-w-md bg-[#0a1530] border border-white/10 rounded-2xl p-8 shadow-2xl shadow-black/40"
        data-testid="owner-login-form"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-lg bg-[#10b7c0]/15 border border-[#10b7c0]/30 flex items-center justify-center">
            <ShieldCheck className="h-5 w-5 text-[#10b7c0]" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#10b7c0] font-body font-semibold">Platform</p>
            <h1 className="text-xl font-heading font-bold text-[#f1fcff] -mt-0.5">Owner Portal</h1>
          </div>
        </div>

        <label className="block text-[11px] uppercase tracking-wider font-body font-semibold text-[#ebeef5]/70 mb-1.5">Email</label>
        <input
          type="email"
          required
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full bg-[#040b1d] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#f1fcff] font-body focus:outline-none focus:border-[#10b7c0]/50"
          data-testid="owner-login-email"
        />

        <label className="block text-[11px] uppercase tracking-wider font-body font-semibold text-[#ebeef5]/70 mb-1.5 mt-4">Password</label>
        <input
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full bg-[#040b1d] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#f1fcff] font-body focus:outline-none focus:border-[#10b7c0]/50"
          data-testid="owner-login-password"
        />

        {err && (
          <p className="mt-4 text-xs text-red-400 font-body" data-testid="owner-login-error">{err}</p>
        )}

        <button
          type="submit"
          disabled={busy}
          className="mt-6 w-full bg-[#10b7c0] hover:bg-[#0ea2aa] disabled:opacity-60 text-black font-body font-semibold text-sm rounded-lg py-2.5 transition-colors flex items-center justify-center gap-2"
          data-testid="owner-login-submit"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          Sign in
        </button>

        <p className="mt-6 text-[11px] text-[#ebeef5]/50 font-body text-center">
          Restricted platform access. All sign-in attempts are logged.
        </p>
      </form>
    </div>
  );
}
